import React, { useCallback, useEffect, useState } from 'react';
import ReactDOM from 'react-dom';
import { Tree, Button, Space, message, Popconfirm, Modal, Input } from 'antd';import {
  FileOutlined,
  FolderOutlined,
  SaveOutlined,
  DeleteOutlined,
  ReloadOutlined,
  FileAddOutlined,
  FolderAddOutlined,
  ClearOutlined,
  DownloadOutlined,
} from '@ant-design/icons';
import Editor from '@monaco-editor/react';
import { applicationsApi } from '../services/api-services';
import { useTheme } from '../contexts/ThemeContext';
import './AppFileManager.css';

const { DirectoryTree } = Tree;

const EXT_LANG_MAP: Record<string, string> = {
  js: 'javascript', jsx: 'javascript', ts: 'typescript', tsx: 'typescript',
  py: 'python', html: 'html', css: 'css', json: 'json', yaml: 'yaml', yml: 'yaml',
  md: 'markdown', sh: 'shell', bash: 'shell', xml: 'xml', sql: 'sql',
  toml: 'ini', ini: 'ini', rs: 'rust', go: 'go', java: 'java', cpp: 'cpp', c: 'c',
};

const getLanguage = (path: string): string => {
  const ext = path.split('.').pop()?.toLowerCase() || '';
  return EXT_LANG_MAP[ext] || 'plaintext';
};

interface FileNode {
  name: string;
  path: string;
  type: 'file' | 'directory';
  size?: number;
  children?: FileNode[];
}

interface TreeDataNode {
  title: string;
  key: string;
  icon: React.ReactNode;
  isLeaf: boolean;
  children?: TreeDataNode[];
}

export interface FileManagerApi {
  listFiles: (id: number) => Promise<any>;
  readFile: (id: number, path: string) => Promise<any>;
  writeFile: (id: number, path: string, content: string) => Promise<any>;
  createFile: (id: number, path: string, type: 'file' | 'directory') => Promise<any>;
  deleteFile: (id: number, path: string) => Promise<any>;
}

interface AppFileManagerProps {
  appId: number;
  fileApi?: FileManagerApi;
  defaultExpandAll?: boolean;
  initialFile?: string;
  hiddenNames?: string[];
  allowedNames?: string[];
  rootDir?: string;
  autoOpenFile?: string;
  showCreateButtons?: boolean;
  showDeleteButton?: boolean;
  showClearButton?: boolean;
}

const toTreeData = (nodes: FileNode[], hiddenNames?: string[], allowedNames?: string[]): TreeDataNode[] => {
  const hidden = new Set(hiddenNames || []);
  const allowed = allowedNames ? new Set(allowedNames) : null;
  return nodes
    .filter((node) => !hidden.has(node.name))
    .filter((node) => !allowed || allowed.has(node.name))
    .map((node) => ({
      title: node.name,
      key: node.path,
      icon: node.type === 'directory' ? <FolderOutlined /> : <FileOutlined />,
      isLeaf: node.type === 'file',
      children: node.children ? toTreeData(node.children) : undefined,
    }));
};

const AppFileManager: React.FC<AppFileManagerProps> = ({ appId, fileApi, defaultExpandAll = true, initialFile, hiddenNames, allowedNames, rootDir, autoOpenFile, showCreateButtons = true, showDeleteButton = true, showClearButton = false }) => {
  const api = fileApi || applicationsApi;
  const { theme } = useTheme();
  const [files, setFiles] = useState<FileNode[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedFile, setSelectedFile] = useState<string | null>(null);
  const [fileContent, setFileContent] = useState('');
  const [originalContent, setOriginalContent] = useState('');
  const [saving, setSaving] = useState(false);
  const [newFileModal, setNewFileModal] = useState<{ visible: boolean; type: 'file' | 'directory' }>({ visible: false, type: 'file' });
  const [newFilePath, setNewFilePath] = useState('');

  const fetchFiles = useCallback(async () => {
    setLoading(true);
    try {
      const res: any = await api.listFiles(appId);
      let fileList = res || [];
      if (rootDir) {
        const dirNode = fileList.find((n: FileNode) => n.name === rootDir && n.type === 'directory');
        fileList = dirNode?.children || [];
      }
      setFiles(fileList);
    } catch {
      message.error('获取文件列表失败');
    }
    setLoading(false);
  }, [appId, api, rootDir]);

  useEffect(() => {
    fetchFiles();
  }, [fetchFiles]);

  const handleSelect = async (keys: React.Key[]) => {
    const path = keys[0] as string;
    if (!path) return;

    const node = findNode(files, path);
    if (!node) return;

    setSelectedFile(path);

    if (node.type === 'directory') {
      setFileContent('');
      setOriginalContent('');
      if (autoOpenFile) {
        const childFile = node.children?.find((c) => c.name === autoOpenFile);
        if (childFile) {
          handleSelect([childFile.path]);
          return;
        }
      }
      return;
    }

    try {
      const baseUrl = process.env.REACT_APP_API_URL || '/api/v1';
      const token = localStorage.getItem('token');

      let endpoint: string;
      if (fileApi) {
        endpoint = `${baseUrl}/digital-employees/${appId}/files/content?path=${encodeURIComponent(path)}`;
      } else {
        endpoint = `${baseUrl}/sub_app/${appId}/files/content?path=${encodeURIComponent(path)}`;
      }

      const resp = await fetch(endpoint, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!resp.ok) {
        const err = await resp.json().catch(() => ({ detail: '读取失败' }));
        message.error(err.detail || '读取失败');
        return;
      }
      const content = await resp.text();
      setSelectedFile(path);
      setFileContent(content);
      setOriginalContent(content);
    } catch {
      message.error('读取文件失败');
    }
  };

  // Must be after handleSelect declaration
  useEffect(() => {
    if (initialFile && files.length > 0 && !selectedFile) {
      handleSelect([initialFile]);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [files, initialFile]);

  const findNode = (nodes: FileNode[], path: string): FileNode | null => {
    for (const n of nodes) {
      if (n.path === path) return n;
      if (n.children) {
        const found = findNode(n.children, path);
        if (found) return found;
      }
    }
    return null;
  };

  const handleSave = async () => {
    if (!selectedFile) return;
    setSaving(true);
    try {
      await api.writeFile(appId, selectedFile, fileContent);
      setOriginalContent(fileContent);
      message.success('保存成功');
    } catch {
      message.error('保存失败');
    }
    setSaving(false);
  };

  const handleCreateFile = async () => {
    if (!newFilePath.trim()) {
      message.warning('请输入路径');
      return;
    }
    try {
      await api.createFile(appId, newFilePath.trim(), newFileModal.type);
      message.success('创建成功');
      setNewFileModal({ visible: false, type: 'file' });
      setNewFilePath('');
      fetchFiles();
    } catch (e: any) {
      message.error(e.response?.data?.detail || '创建失败');
    }
  };

  const handleDelete = async () => {
    if (!selectedFile) return;
    try {
      await api.deleteFile(appId, selectedFile);
      message.success('删除成功');
      setSelectedFile(null);
      setFileContent('');
      setOriginalContent('');
      fetchFiles();
    } catch {
      message.error('删除失败');
    }
  };

  const handleClear = async () => {
    if (!rootDir) return;
    try {
      for (const node of files) {
        await api.deleteFile(appId, rootDir + '/' + node.name);
      }
      message.success('清空成功');
      setSelectedFile(null);
      setFileContent('');
      setOriginalContent('');
      fetchFiles();
    } catch {
      message.error('清空失败');
    }
  };

  const isModified = fileContent !== originalContent;
  const treeData = toTreeData(files, hiddenNames, allowedNames);
  const selectedNode = selectedFile ? findNode(files, selectedFile) : null;
  const isDirectory = selectedNode?.type === 'directory';

  const [contextMenu, setContextMenu] = useState<{ path: string; name: string; x: number; y: number } | null>(null);

  const handleDownload = async (path: string, name: string) => {
    try {
      const baseUrl = process.env.REACT_APP_API_URL || '/api/v1';
      const token = localStorage.getItem('token');
      const endpoint = fileApi
        ? `${baseUrl}/digital-employees/${appId}/files/content?path=${encodeURIComponent(path)}`
        : `${baseUrl}/sub_app/${appId}/files/content?path=${encodeURIComponent(path)}`;
      const resp = await fetch(endpoint, { headers: { Authorization: `Bearer ${token}` } });
      if (!resp.ok) { message.error('下载失败'); return; }
      const blob = await resp.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = name;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch { message.error('下载失败'); }
  };

  return (
    <div className="app-file-manager">
      <div className="app-file-manager-sidebar">
        <div className="app-file-manager-toolbar">
          <Space size={4}>
            <Button size="small" icon={<ReloadOutlined />} onClick={fetchFiles} loading={loading} title="刷新" />
            {showCreateButtons && (
              <>
                <Button size="small" icon={<FileAddOutlined />} onClick={() => { setNewFileModal({ visible: true, type: 'file' }); setNewFilePath(''); }} title="新建文件" />
                <Button size="small" icon={<FolderAddOutlined />} onClick={() => { setNewFileModal({ visible: true, type: 'directory' }); setNewFilePath(''); }} title="新建文件夹" />
              </>
            )}
            {showDeleteButton && selectedFile && (
              <Popconfirm title={isDirectory ? "确定删除此目录及其所有内容？" : "确定删除此文件？"} onConfirm={handleDelete} okText="确定" cancelText="取消">
                <Button size="small" danger icon={<DeleteOutlined />} title="删除" />
              </Popconfirm>
            )}
            {showClearButton && files.length > 0 && (
              <Popconfirm title="确定清空当前目录下所有内容？" onConfirm={handleClear} okText="确定" cancelText="取消">
                <Button size="small" danger icon={<ClearOutlined />} title="清空" />
              </Popconfirm>
            )}
          </Space>
        </div>
        <div className="app-file-manager-tree">
          {treeData.length > 0 ? (
            <DirectoryTree
              treeData={treeData}
              onSelect={handleSelect}
              selectedKeys={selectedFile ? [selectedFile] : []}
              defaultExpandAll={defaultExpandAll}
              onRightClick={({ event, node }) => {
                event.preventDefault();
                const nodeData = findNode(files, node.key as string);
                if (nodeData?.type === 'file') {
                  setContextMenu({ path: node.key as string, name: node.title as string, x: event.clientX, y: event.clientY });
                }
              }}
            />
          ) : (
            <div className="app-file-manager-empty">暂无文件</div>
          )}
        </div>
      </div>
      {contextMenu && ReactDOM.createPortal(
        <div
          style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 999 }}
          onClick={() => setContextMenu(null)}
          onContextMenu={(e) => { e.preventDefault(); setContextMenu(null); }}
        >
          <div
            className={`file-context-menu${theme === 'glass' ? ' theme-glass' : ''}`}
            style={{ top: contextMenu.y, left: contextMenu.x }}
            onClick={(e) => e.stopPropagation()}
          >
            <div
              className="file-context-menu-item"
              onClick={() => { handleDownload(contextMenu.path, contextMenu.name); setContextMenu(null); }}
            >
              <DownloadOutlined /> 下载
            </div>
          </div>
        </div>,
        document.body
      )}
      <div className="app-file-manager-editor">
        {selectedFile ? (
          <>
            <div className="app-file-manager-editor-header">
              <span className="app-file-manager-filename">
                {selectedFile}
                {!isDirectory && isModified && <span className="app-file-manager-modified"> (已修改)</span>}
              </span>
              {!isDirectory && (
                <Button size="small" type="primary" icon={<SaveOutlined />} onClick={handleSave} loading={saving} disabled={!isModified}>
                  保存
                </Button>
              )}
            </div>
            {isDirectory ? (
              <div className="app-file-manager-placeholder">
                当前选中目录: {selectedFile}
              </div>
            ) : (
              <div style={{ flex: 1, minHeight: 0 }}>
                <Editor
                  height="100%"
                  language={getLanguage(selectedFile)}
                  theme={theme === 'glass' ? 'vs-dark' : 'light'}
                  value={fileContent}
                  onChange={(val) => setFileContent(val || '')}
                  options={{ minimap: { enabled: false }, fontSize: 13, scrollBeyondLastLine: false, padding: { top: 8 } }}
                />
              </div>
            )}
          </>
        ) : (
          <div className="app-file-manager-placeholder">
            选择左侧文件查看和编辑
          </div>
        )}
      </div>

      <Modal
        title={newFileModal.type === 'file' ? '新建文件' : '新建文件夹'}
        open={newFileModal.visible}
        onOk={handleCreateFile}
        onCancel={() => setNewFileModal({ visible: false, type: 'file' })}
        okText="创建"
        cancelText="取消"
      >
        <Input
          placeholder={newFileModal.type === 'file' ? '例如: static/new-page.html' : '例如: static/assets'}
          value={newFilePath}
          onChange={(e) => setNewFilePath(e.target.value)}
          onPressEnter={handleCreateFile}
        />
      </Modal>
    </div>
  );
};

export default AppFileManager;
